import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Brain, Building2, Save, Plus, Trash2, RefreshCw, ChevronDown, ChevronRight,
  AlertTriangle, CheckCircle2, BookOpen, Shield, MessageSquare, ListChecks,
  FileText, Upload, Download, Eye, File as FileIcon, Target, Sparkles, Trophy,
  Zap, Users, Filter
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger
} from "../components/ui/accordion";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import Layout from "../components/Layout";
import { API } from "../App";
import { toast } from "sonner";

const AITrainingPage = () => {
  const [tab, setTab] = useState("company");
  const [loading, setLoading] = useState(false);

  // Company profile
  const [company, setCompany] = useState({
    company_name: "", developer_brand: "", established_year: "",
    track_record: "", usps: [], headquarters: "", website: "",
    tagline: "", tone_guidelines: "", disclaimers: ""
  });
  const [newUsp, setNewUsp] = useState("");

  // Guidelines
  const [guidelines, setGuidelines] = useState({
    dos: [], donts: [], custom_greeting: "",
    fallback_message: "Let me connect you with our team for more details on that.",
    restricted_topics: [], approved_scripts: []
  });
  const [newDo, setNewDo] = useState("");
  const [newDont, setNewDont] = useState("");
  const [newTopic, setNewTopic] = useState("");

  // Project briefs
  const [projects, setProjects] = useState([]);
  const [briefs, setBriefs] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [currentBrief, setCurrentBrief] = useState(null);
  const [newFaq, setNewFaq] = useState({ question: "", answer: "" });
  const [newObjection, setNewObjection] = useState({ objection: "", response: "" });
  const [newDoNotSay, setNewDoNotSay] = useState("");
  const [newScript, setNewScript] = useState({ scenario: "", script: "" });

  // Documents
  const [documents, setDocuments] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docFile, setDocFile] = useState(null);
  const [docCategory, setDocCategory] = useState("general");
  const [docDescription, setDocDescription] = useState("");
  const [docTargetRoles, setDocTargetRoles] = useState(["all"]);
  const [docPreview, setDocPreview] = useState(null);

  // Strategy Rules
  const [strategyRules, setStrategyRules] = useState([]);
  const [editingRule, setEditingRule] = useState(null);
  const [newRule, setNewRule] = useState({
    name: "",
    description: "",
    target_roles: ["all"],
    priority: 50,
    is_active: true,
    conditions: {
      lead_score: [],
      lead_stage: [],
      days_inactive_min: null,
      days_inactive_max: null,
      source: [],
    },
    strategy: {
      approach: "",
      message_template: "",
      call_script: "",
      talking_points: [],
      offer_suggestions: [],
      timing_advice: "",
    },
  });

  // Success Examples
  const [successExamples, setSuccessExamples] = useState([]);
  const [newExample, setNewExample] = useState({
    title: "",
    category: "re-engagement",
    target_roles: ["all"],
    approach_used: "",
    message_sent: "",
    key_phrases: [],
    outcome: { result: "", notes: "" },
    why_it_worked: "",
    tags: [],
  });
  const [strategyStats, setStrategyStats] = useState(null);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [compRes, guideRes, briefsRes, projRes, docsRes, rulesRes, examplesRes, statsRes] = await Promise.all([
        axios.get(`${API}/ai-knowledge/company`),
        axios.get(`${API}/ai-knowledge/guidelines`),
        axios.get(`${API}/ai-knowledge/project-briefs`),
        axios.get(`${API}/projects`),
        axios.get(`${API}/ai-documents`),
        axios.get(`${API}/ai-strategies/rules`),
        axios.get(`${API}/ai-strategies/success-examples`),
        axios.get(`${API}/ai-strategies/stats`),
      ]);
      setCompany({ ...company, ...compRes.data });
      setGuidelines({ ...guidelines, ...guideRes.data });
      setBriefs(briefsRes.data);
      setProjects(projRes.data);
      setDocuments(docsRes.data);
      setStrategyRules(rulesRes.data);
      setSuccessExamples(examplesRes.data);
      setStrategyStats(statsRes.data);
    } catch {
      toast.error("Failed to load AI training data");
    } finally {
      setLoading(false);
    }
  };

  const saveCompany = async () => {
    try {
      const res = await axios.put(`${API}/ai-knowledge/company`, company);
      setCompany({ ...company, ...res.data });
      toast.success("Company profile saved");
    } catch { toast.error("Failed to save"); }
  };

  const saveGuidelines = async () => {
    try {
      const res = await axios.put(`${API}/ai-knowledge/guidelines`, guidelines);
      setGuidelines({ ...guidelines, ...res.data });
      toast.success("AI guidelines saved");
    } catch { toast.error("Failed to save"); }
  };

  const loadBrief = async (projectId) => {
    setSelectedProject(projectId);
    try {
      const res = await axios.get(`${API}/ai-knowledge/project-briefs/${projectId}`);
      setCurrentBrief(res.data);
    } catch { toast.error("Failed to load brief"); }
  };

  const saveBrief = async () => {
    if (!selectedProject || !currentBrief) return;
    try {
      const res = await axios.put(`${API}/ai-knowledge/project-briefs/${selectedProject}`, currentBrief);
      setCurrentBrief(res.data);
      toast.success("Project brief saved");
      const briefsRes = await axios.get(`${API}/ai-knowledge/project-briefs`);
      setBriefs(briefsRes.data);
    } catch { toast.error("Failed to save brief"); }
  };

  const uploadDocument = async () => {
    if (!docFile) return;
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append("file", docFile);
      fd.append("category", docCategory);
      fd.append("description", docDescription);
      fd.append("target_roles", docTargetRoles.join(","));
      const res = await axios.post(`${API}/ai-documents/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      toast.success(`Uploaded! ${res.data.extraction_summary.words} words extracted from ${res.data.extraction_summary.pages} pages`);
      setDocFile(null);
      setDocDescription("");
      setDocCategory("general");
      setDocTargetRoles(["all"]);
      const docsRes = await axios.get(`${API}/ai-documents`);
      setDocuments(docsRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingDoc(false);
    }
  };

  const deleteDocument = async (docId) => {
    try {
      await axios.delete(`${API}/ai-documents/${docId}`);
      toast.success("Document removed");
      setDocuments(documents.filter(d => d.doc_id !== docId));
    } catch { toast.error("Failed to delete"); }
  };

  const previewDocument = async (docId) => {
    try {
      const res = await axios.get(`${API}/ai-documents/${docId}`);
      setDocPreview(res.data);
    } catch { toast.error("Failed to load preview"); }
  };

  // Strategy Rules CRUD
  const saveStrategyRule = async (rule) => {
    try {
      if (rule.rule_id) {
        await axios.put(`${API}/ai-strategies/rules/${rule.rule_id}`, rule);
        toast.success("Strategy updated");
      } else {
        await axios.post(`${API}/ai-strategies/rules`, rule);
        toast.success("Strategy created");
      }
      setEditingRule(null);
      setNewRule({
        name: "", description: "", target_roles: ["all"], priority: 50, is_active: true,
        conditions: { lead_score: [], lead_stage: [], days_inactive_min: null, days_inactive_max: null, source: [] },
        strategy: { approach: "", message_template: "", call_script: "", talking_points: [], offer_suggestions: [], timing_advice: "" },
      });
      const rulesRes = await axios.get(`${API}/ai-strategies/rules`);
      setStrategyRules(rulesRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save strategy");
    }
  };

  const deleteStrategyRule = async (ruleId) => {
    try {
      await axios.delete(`${API}/ai-strategies/rules/${ruleId}`);
      toast.success("Strategy deleted");
      setStrategyRules(strategyRules.filter(r => r.rule_id !== ruleId));
    } catch { toast.error("Failed to delete"); }
  };

  // Success Examples CRUD
  const saveSuccessExample = async () => {
    if (!newExample.title) {
      toast.error("Please provide a title");
      return;
    }
    try {
      await axios.post(`${API}/ai-strategies/success-examples`, newExample);
      toast.success("Success example added");
      setNewExample({
        title: "", category: "re-engagement", target_roles: ["all"], approach_used: "",
        message_sent: "", key_phrases: [], outcome: { result: "", notes: "" }, why_it_worked: "", tags: [],
      });
      const examplesRes = await axios.get(`${API}/ai-strategies/success-examples`);
      setSuccessExamples(examplesRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save example");
    }
  };

  const deleteSuccessExample = async (exampleId) => {
    try {
      await axios.delete(`${API}/ai-strategies/success-examples/${exampleId}`);
      toast.success("Example removed");
      setSuccessExamples(successExamples.filter(e => e.example_id !== exampleId));
    } catch { toast.error("Failed to delete"); }
  };

  const addToList = (field, value, setter, stateObj, setStateObj) => {
    if (!value.trim()) return;
    setStateObj({ ...stateObj, [field]: [...(stateObj[field] || []), value.trim()] });
    setter("");
  };

  const removeFromList = (field, idx, stateObj, setStateObj) => {
    const updated = [...(stateObj[field] || [])];
    updated.splice(idx, 1);
    setStateObj({ ...stateObj, [field]: updated });
  };

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8 max-w-5xl mx-auto space-y-6" data-testid="ai-training-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']" data-testid="ai-training-title">
              AI Training & Knowledge Base
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Train your AI to only use your company data. All AI features pull from this knowledge base.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading} data-testid="ai-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="w-full h-auto flex overflow-x-auto gap-1 p-1" data-testid="ai-training-tabs">
            <TabsTrigger value="company" className="text-xs whitespace-nowrap px-2 py-2 flex-shrink-0">
              <Building2 className="w-4 h-4 mr-1" /> Company
            </TabsTrigger>
            <TabsTrigger value="guidelines" className="text-xs whitespace-nowrap px-2 py-2 flex-shrink-0">
              <Shield className="w-4 h-4 mr-1" /> Guidelines
            </TabsTrigger>
            <TabsTrigger value="projects" className="text-xs whitespace-nowrap px-2 py-2 flex-shrink-0">
              <BookOpen className="w-4 h-4 mr-1" /> Briefs
            </TabsTrigger>
            <TabsTrigger value="documents" className="text-xs whitespace-nowrap px-2 py-2 flex-shrink-0">
              <FileText className="w-4 h-4 mr-1" /> Docs
              {documents.length > 0 && <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">{documents.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="strategies" className="text-xs whitespace-nowrap px-2 py-2 flex-shrink-0">
              <Target className="w-4 h-4 mr-1" /> Strategies
              {strategyRules.length > 0 && <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">{strategyRules.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="success" className="text-xs whitespace-nowrap px-2 py-2 flex-shrink-0">
              <Trophy className="w-4 h-4 mr-1" /> Success
              {successExamples.length > 0 && <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">{successExamples.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="widget" className="text-xs whitespace-nowrap px-2 py-2 flex-shrink-0">
              <MessageSquare className="w-4 h-4 mr-1" /> Deploy
            </TabsTrigger>
          </TabsList>

          {/* ═══ COMPANY PROFILE TAB ═══ */}
          <TabsContent value="company" className="mt-4 space-y-4">
            <Card>
              <CardContent className="p-5 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Company Name</Label>
                    <Input value={company.company_name} onChange={(e) => setCompany({ ...company, company_name: e.target.value })} placeholder="e.g., Lodha Group" data-testid="company-name-input" />
                  </div>
                  <div>
                    <Label>Developer Brand</Label>
                    <Input value={company.developer_brand} onChange={(e) => setCompany({ ...company, developer_brand: e.target.value })} placeholder="e.g., Macrotech Developers Ltd" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <Label>Established Year</Label>
                    <Input type="number" value={company.established_year || ""} onChange={(e) => setCompany({ ...company, established_year: e.target.value })} placeholder="e.g., 1995" />
                  </div>
                  <div>
                    <Label>Headquarters</Label>
                    <Input value={company.headquarters} onChange={(e) => setCompany({ ...company, headquarters: e.target.value })} placeholder="e.g., Mumbai" />
                  </div>
                  <div>
                    <Label>Website</Label>
                    <Input value={company.website} onChange={(e) => setCompany({ ...company, website: e.target.value })} placeholder="https://..." />
                  </div>
                </div>
                <div>
                  <Label>Tagline</Label>
                  <Input value={company.tagline} onChange={(e) => setCompany({ ...company, tagline: e.target.value })} placeholder="Building Homes, Building Trust" />
                </div>
                <div>
                  <Label>Track Record</Label>
                  <Textarea value={company.track_record} onChange={(e) => setCompany({ ...company, track_record: e.target.value })} placeholder="e.g., 30+ years, 40,000+ homes delivered, 12 cities..." rows={2} />
                </div>

                {/* USPs */}
                <div>
                  <Label>Company USPs</Label>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {(company.usps || []).map((u, i) => (
                      <Badge key={i} variant="secondary" className="gap-1">
                        {u}
                        <button onClick={() => removeFromList("usps", i, company, setCompany)} className="ml-1 text-slate-400 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
                      </Badge>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input value={newUsp} onChange={(e) => setNewUsp(e.target.value)} placeholder="Add a USP..." onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addToList("usps", newUsp, setNewUsp, company, setCompany))} />
                    <Button size="sm" variant="outline" onClick={() => addToList("usps", newUsp, setNewUsp, company, setCompany)}><Plus className="w-4 h-4" /></Button>
                  </div>
                </div>

                <div>
                  <Label>Communication Tone</Label>
                  <Textarea value={company.tone_guidelines} onChange={(e) => setCompany({ ...company, tone_guidelines: e.target.value })} placeholder="e.g., Professional but warm. Use 'we' not 'I'. Always be optimistic about market..." rows={2} />
                </div>
                <div>
                  <Label>Legal Disclaimers</Label>
                  <Textarea value={company.disclaimers} onChange={(e) => setCompany({ ...company, disclaimers: e.target.value })} placeholder="Standard disclaimers the AI should mention when discussing pricing/legal..." rows={2} />
                </div>
                <Button onClick={saveCompany} data-testid="save-company-btn"><Save className="w-4 h-4 mr-2" /> Save Company Profile</Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ═══ AI GUIDELINES TAB ═══ */}
          <TabsContent value="guidelines" className="mt-4 space-y-4">
            <Card>
              <CardContent className="p-5 space-y-4">
                <div>
                  <Label>Custom Chatbot Greeting</Label>
                  <Textarea value={guidelines.custom_greeting} onChange={(e) => setGuidelines({ ...guidelines, custom_greeting: e.target.value })} placeholder="Welcome to [Company]! I'm your virtual assistant..." rows={2} data-testid="custom-greeting-input" />
                </div>
                <div>
                  <Label>Fallback Message (when AI can't answer)</Label>
                  <Input value={guidelines.fallback_message} onChange={(e) => setGuidelines({ ...guidelines, fallback_message: e.target.value })} data-testid="fallback-msg-input" />
                </div>

                {/* DOs */}
                <div>
                  <Label className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> DOs (AI should do)</Label>
                  <div className="space-y-1 mb-2">
                    {(guidelines.dos || []).map((d, i) => (
                      <div key={i} className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded px-3 py-1.5 text-sm">
                        <span className="flex-1">{d}</span>
                        <button onClick={() => removeFromList("dos", i, guidelines, setGuidelines)} className="text-slate-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input value={newDo} onChange={(e) => setNewDo(e.target.value)} placeholder="e.g., Always mention RERA compliance" onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addToList("dos", newDo, setNewDo, guidelines, setGuidelines))} />
                    <Button size="sm" variant="outline" onClick={() => addToList("dos", newDo, setNewDo, guidelines, setGuidelines)}><Plus className="w-4 h-4" /></Button>
                  </div>
                </div>

                {/* DON'Ts */}
                <div>
                  <Label className="flex items-center gap-1.5"><AlertTriangle className="w-4 h-4 text-red-500" /> DON'Ts (AI must NOT do)</Label>
                  <div className="space-y-1 mb-2">
                    {(guidelines.donts || []).map((d, i) => (
                      <div key={i} className="flex items-center gap-2 bg-red-50 border border-red-200 rounded px-3 py-1.5 text-sm">
                        <span className="flex-1">{d}</span>
                        <button onClick={() => removeFromList("donts", i, guidelines, setGuidelines)} className="text-slate-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input value={newDont} onChange={(e) => setNewDont(e.target.value)} placeholder="e.g., Never discuss competitor pricing" onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addToList("donts", newDont, setNewDont, guidelines, setGuidelines))} />
                    <Button size="sm" variant="outline" onClick={() => addToList("donts", newDont, setNewDont, guidelines, setGuidelines)}><Plus className="w-4 h-4" /></Button>
                  </div>
                </div>

                {/* Restricted Topics */}
                <div>
                  <Label>Restricted Topics (AI will refuse to discuss)</Label>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {(guidelines.restricted_topics || []).map((t, i) => (
                      <Badge key={i} variant="destructive" className="gap-1">
                        {t}
                        <button onClick={() => removeFromList("restricted_topics", i, guidelines, setGuidelines)} className="ml-1"><Trash2 className="w-3 h-3" /></button>
                      </Badge>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input value={newTopic} onChange={(e) => setNewTopic(e.target.value)} placeholder="e.g., Internal commissions, competitor names" onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addToList("restricted_topics", newTopic, setNewTopic, guidelines, setGuidelines))} />
                    <Button size="sm" variant="outline" onClick={() => addToList("restricted_topics", newTopic, setNewTopic, guidelines, setGuidelines)}><Plus className="w-4 h-4" /></Button>
                  </div>
                </div>

                {/* Approved Scripts */}
                <div>
                  <Label className="flex items-center gap-1.5"><ListChecks className="w-4 h-4 text-blue-600" /> Approved Scripts</Label>
                  <div className="space-y-2 mb-2">
                    {(guidelines.approved_scripts || []).map((s, i) => (
                      <div key={i} className="bg-blue-50 border border-blue-200 rounded p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-xs font-semibold text-blue-700">{s.scenario}</p>
                            <p className="text-sm text-slate-700 mt-1">{s.script}</p>
                          </div>
                          <button onClick={() => removeFromList("approved_scripts", i, guidelines, setGuidelines)} className="text-slate-400 hover:text-red-500 flex-shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-1 gap-2">
                    <Input value={newScript.scenario} onChange={(e) => setNewScript({ ...newScript, scenario: e.target.value })} placeholder="Scenario (e.g., Customer asks about possession delay)" />
                    <Textarea value={newScript.script} onChange={(e) => setNewScript({ ...newScript, script: e.target.value })} placeholder="Approved response script..." rows={2} />
                    <Button size="sm" variant="outline" onClick={() => {
                      if (newScript.scenario && newScript.script) {
                        setGuidelines({ ...guidelines, approved_scripts: [...(guidelines.approved_scripts || []), { ...newScript }] });
                        setNewScript({ scenario: "", script: "" });
                      }
                    }}><Plus className="w-4 h-4 mr-1" /> Add Script</Button>
                  </div>
                </div>

                <Button onClick={saveGuidelines} data-testid="save-guidelines-btn"><Save className="w-4 h-4 mr-2" /> Save Guidelines</Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ═══ PROJECT BRIEFS TAB ═══ */}
          <TabsContent value="projects" className="mt-4 space-y-4">
            <ProjectBriefsTab
              projects={projects}
              briefs={briefs}
              selectedProject={selectedProject}
              currentBrief={currentBrief}
              setCurrentBrief={setCurrentBrief}
              newFaq={newFaq}
              setNewFaq={setNewFaq}
              newObjection={newObjection}
              setNewObjection={setNewObjection}
              newDoNotSay={newDoNotSay}
              setNewDoNotSay={setNewDoNotSay}
              loadBrief={loadBrief}
              saveBrief={saveBrief}
            />
          </TabsContent>

          {/* ═══ DOCUMENTS TAB ═══ */}
          <TabsContent value="documents" className="mt-4 space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-500" />
                  Training Documents
                </CardTitle>
                <p className="text-xs text-slate-500 mt-1">
                  Upload brochures, sales manuals, competitor analysis, or any PDF. The AI will extract and learn from the content.
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Upload Form */}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm">Category</Label>
                      <Select value={docCategory} onValueChange={setDocCategory}>
                        <SelectTrigger data-testid="doc-category-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">General</SelectItem>
                          <SelectItem value="brochure">Brochure</SelectItem>
                          <SelectItem value="competitor">Competitor Analysis</SelectItem>
                          <SelectItem value="sales_manual">Sales Training Manual</SelectItem>
                          <SelectItem value="re_engagement">Re-engagement Strategies</SelectItem>
                          <SelectItem value="pricing">Pricing Document</SelectItem>
                          <SelectItem value="legal">Legal / RERA</SelectItem>
                          <SelectItem value="market_research">Market Research</SelectItem>
                          <SelectItem value="floor_plan">Floor Plans</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm">Description</Label>
                      <Input
                        value={docDescription}
                        onChange={(e) => setDocDescription(e.target.value)}
                        placeholder="e.g., Tower A brochure, Q1 2026 pricing..."
                        data-testid="doc-description-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm flex items-center gap-2">
                      <Users className="w-4 h-4" /> Target Roles (who should see this)
                    </Label>
                    <div className="flex flex-wrap gap-2 mt-1.5">
                      {[
                        { value: "all", label: "All Roles" },
                        { value: "telecaller", label: "Telecaller" },
                        { value: "sales_rep", label: "Sales Rep" },
                        { value: "sales_manager", label: "Manager" },
                      ].map(role => (
                        <label key={role.value} className="flex items-center gap-1.5 text-xs cursor-pointer">
                          <Checkbox
                            checked={docTargetRoles.includes(role.value)}
                            onCheckedChange={(checked) => {
                              if (role.value === "all") {
                                setDocTargetRoles(checked ? ["all"] : []);
                              } else {
                                const filtered = docTargetRoles.filter(r => r !== "all" && r !== role.value);
                                setDocTargetRoles(checked ? [...filtered, role.value] : filtered);
                              }
                            }}
                          />
                          {role.label}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm">PDF File</Label>
                    <Input
                      type="file"
                      accept=".pdf"
                      onChange={(e) => setDocFile(e.target.files?.[0] || null)}
                      data-testid="doc-file-input"
                      className="cursor-pointer"
                    />
                    {docFile && (
                      <p className="text-xs text-slate-500 mt-1">
                        {docFile.name} ({(docFile.size / (1024 * 1024)).toFixed(1)} MB)
                      </p>
                    )}
                  </div>
                  <Button
                    onClick={uploadDocument}
                    disabled={!docFile || uploadingDoc}
                    data-testid="upload-doc-btn"
                  >
                    {uploadingDoc ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                    {uploadingDoc ? "Extracting text..." : "Upload & Extract"}
                  </Button>
                </div>

                {/* Document List */}
                {documents.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No documents uploaded yet</p>
                    <p className="text-xs">Upload PDFs to train the AI with your sales content</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {documents.map((doc) => (
                      <div key={doc.doc_id} className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg px-4 py-3 hover:border-slate-300 transition-colors" data-testid={`doc-item-${doc.doc_id}`}>
                        <div className="w-9 h-9 bg-red-50 rounded-lg flex items-center justify-center flex-shrink-0">
                          <FileIcon className="w-5 h-5 text-red-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{doc.filename}</p>
                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            <Badge variant="secondary" className="text-[10px] h-4">{doc.category?.replace(/_/g, " ")}</Badge>
                            <span className="text-[10px] text-slate-400">{doc.page_count} pages</span>
                            <span className="text-[10px] text-slate-400">{doc.word_count?.toLocaleString()} words</span>
                            <span className="text-[10px] text-slate-400">{(doc.file_size / (1024 * 1024)).toFixed(1)} MB</span>
                          </div>
                          {doc.description && <p className="text-xs text-slate-500 mt-0.5 truncate">{doc.description}</p>}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => previewDocument(doc.doc_id)} data-testid={`preview-doc-${doc.doc_id}`}>
                            <Eye className="w-4 h-4 text-slate-400" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-400 hover:text-red-600" onClick={() => deleteDocument(doc.doc_id)} data-testid={`delete-doc-${doc.doc_id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Text Preview Dialog */}
                {docPreview && (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="doc-preview">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{docPreview.filename}</p>
                        <p className="text-xs text-slate-500">{docPreview.full_text_length?.toLocaleString()} characters extracted</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setDocPreview(null)}>Close</Button>
                    </div>
                    <div className="bg-white border rounded-lg p-3 max-h-64 overflow-y-auto">
                      <pre className="text-xs text-slate-600 whitespace-pre-wrap font-sans leading-relaxed">
                        {docPreview.text_preview}
                      </pre>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ═══ WIDGET DEPLOY TAB ═══ */}
          <TabsContent value="widget" className="mt-4 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-amber-500" />
                  Deploy Chatbot on Your Website
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-sm text-slate-600">
                  Copy and paste this script tag into your website's HTML, just before the closing <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">&lt;/body&gt;</code> tag. The chatbot will appear as a floating bubble in the bottom-right corner.
                </p>

                <div>
                  <Label className="text-sm font-semibold mb-2 block">Embed Code</Label>
                  <div className="bg-slate-900 rounded-lg p-4 relative">
                    <pre className="text-emerald-400 text-xs overflow-x-auto whitespace-pre-wrap">
{`<script
  src="${window.location.origin}/chatbot-widget.js"
  data-host="${window.location.origin}"
  data-color="#0F172A"
  data-accent="#F59E0B"
  data-position="right">
</script>`}
                    </pre>
                    <Button
                      size="sm"
                      variant="secondary"
                      className="absolute top-2 right-2 text-xs h-7"
                      onClick={() => {
                        navigator.clipboard.writeText(
                          `<script src="${window.location.origin}/chatbot-widget.js" data-host="${window.location.origin}" data-color="#0F172A" data-accent="#F59E0B" data-position="right"></script>`
                        );
                        toast.success("Copied to clipboard!");
                      }}
                      data-testid="copy-embed-btn"
                    >
                      Copy
                    </Button>
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-semibold mb-2 block">Customization Options</Label>
                  <div className="overflow-x-auto">
                    <table className="text-sm w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 pr-4 text-slate-600 font-medium">Attribute</th>
                          <th className="text-left py-2 pr-4 text-slate-600 font-medium">Default</th>
                          <th className="text-left py-2 text-slate-600 font-medium">Description</th>
                        </tr>
                      </thead>
                      <tbody className="text-slate-500">
                        <tr className="border-b border-slate-100"><td className="py-2 pr-4 font-mono text-xs text-slate-700">data-host</td><td className="py-2 pr-4 text-xs">Your CRM URL</td><td className="py-2 text-xs">Backend URL for API calls</td></tr>
                        <tr className="border-b border-slate-100"><td className="py-2 pr-4 font-mono text-xs text-slate-700">data-color</td><td className="py-2 pr-4 text-xs">#0F172A</td><td className="py-2 text-xs">Primary button/header color</td></tr>
                        <tr className="border-b border-slate-100"><td className="py-2 pr-4 font-mono text-xs text-slate-700">data-accent</td><td className="py-2 pr-4 text-xs">#F59E0B</td><td className="py-2 text-xs">Accent/icon color</td></tr>
                        <tr><td className="py-2 pr-4 font-mono text-xs text-slate-700">data-position</td><td className="py-2 pr-4 text-xs">right</td><td className="py-2 text-xs">Widget position (left or right)</td></tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-amber-800">How it works:</p>
                  <ul className="text-xs text-amber-700 mt-2 space-y-1 list-disc list-inside">
                    <li>The widget auto-loads your company name and greeting from the <strong>Company Profile</strong> tab</li>
                    <li>All AI responses are restricted to your <strong>Knowledge Base</strong> — no hallucinated answers</li>
                    <li>Leads captured from chat conversations appear automatically in your CRM</li>
                    <li>Chat sessions are visible in the <strong>Chatbot</strong> page for your team</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ═══ STRATEGIES TAB ═══ */}
          <TabsContent value="strategies" className="mt-4 space-y-4">
            <StrategiesTab
              rules={strategyRules}
              editingRule={editingRule}
              setEditingRule={setEditingRule}
              newRule={newRule}
              setNewRule={setNewRule}
              saveRule={saveStrategyRule}
              deleteRule={deleteStrategyRule}
              stats={strategyStats}
            />
          </TabsContent>

          {/* ═══ SUCCESS EXAMPLES TAB ═══ */}
          <TabsContent value="success" className="mt-4 space-y-4">
            <SuccessExamplesTab
              examples={successExamples}
              newExample={newExample}
              setNewExample={setNewExample}
              saveExample={saveSuccessExample}
              deleteExample={deleteSuccessExample}
              stats={strategyStats}
            />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

/* ─── Project Briefs Sub-component ─── */
const ProjectBriefsTab = ({ projects, briefs, selectedProject, currentBrief, setCurrentBrief, newFaq, setNewFaq, newObjection, setNewObjection, newDoNotSay, setNewDoNotSay, loadBrief, saveBrief }) => (
  <Card>
    <CardHeader className="pb-3">
      <CardTitle className="text-base font-semibold">Select a Project to Train</CardTitle>
    </CardHeader>
    <CardContent className="space-y-4">
      <Select value={selectedProject} onValueChange={loadBrief}>
        <SelectTrigger data-testid="brief-project-select">
          <SelectValue placeholder="Choose a project..." />
        </SelectTrigger>
        <SelectContent>
          {projects.map(p => (
            <SelectItem key={p.project_id} value={p.project_id}>
              {p.name}
              {briefs.some(b => b.project_id === p.project_id) && " (has brief)"}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {currentBrief && (
        <div className="space-y-4 border-t pt-4">
          <div>
            <Label>Target Audience</Label>
            <Textarea value={currentBrief.target_audience || ""} onChange={(e) => setCurrentBrief({ ...currentBrief, target_audience: e.target.value })} placeholder="e.g., Young professionals 28-40, HHI 25L+, first-time buyers..." rows={2} />
          </div>
          <div>
            <Label>Key USPs</Label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {(currentBrief.key_usps || []).map((u, i) => (
                <Badge key={i} variant="secondary" className="gap-1">
                  {u}
                  <button onClick={() => { const arr = [...currentBrief.key_usps]; arr.splice(i, 1); setCurrentBrief({ ...currentBrief, key_usps: arr }); }} className="ml-1 text-slate-400 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input placeholder="Add USP..." onKeyDown={(e) => { if (e.key === "Enter" && e.target.value.trim()) { setCurrentBrief({ ...currentBrief, key_usps: [...(currentBrief.key_usps || []), e.target.value.trim()] }); e.target.value = ""; } }} />
            </div>
          </div>
          <div>
            <Label>Selling Points</Label>
            <Textarea value={currentBrief.selling_points || ""} onChange={(e) => setCurrentBrief({ ...currentBrief, selling_points: e.target.value })} placeholder="Detailed selling points, unique features..." rows={3} />
          </div>
          <div>
            <Label>Pricing Rationale</Label>
            <Textarea value={currentBrief.pricing_rationale || ""} onChange={(e) => setCurrentBrief({ ...currentBrief, pricing_rationale: e.target.value })} placeholder="Why is this priced this way?" rows={2} />
          </div>
          <div>
            <Label>Competitor Talking Points</Label>
            <Textarea value={currentBrief.competitor_talking_points || ""} onChange={(e) => setCurrentBrief({ ...currentBrief, competitor_talking_points: e.target.value })} placeholder="How to position against competitors..." rows={2} />
          </div>

          {/* FAQs */}
          <div>
            <Label>FAQs</Label>
            <div className="space-y-2 mb-2">
              {(currentBrief.faqs || []).map((f, i) => (
                <div key={i} className="bg-slate-50 border rounded p-3 relative">
                  <button onClick={() => { const arr = [...currentBrief.faqs]; arr.splice(i, 1); setCurrentBrief({ ...currentBrief, faqs: arr }); }} className="absolute top-2 right-2 text-slate-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                  <p className="text-xs font-semibold text-slate-700">Q: {f.question}</p>
                  <p className="text-xs text-slate-500 mt-1">A: {f.answer}</p>
                </div>
              ))}
            </div>
            <div className="grid gap-2">
              <Input value={newFaq.question} onChange={(e) => setNewFaq({ ...newFaq, question: e.target.value })} placeholder="Question..." />
              <Textarea value={newFaq.answer} onChange={(e) => setNewFaq({ ...newFaq, answer: e.target.value })} placeholder="Answer..." rows={2} />
              <Button size="sm" variant="outline" onClick={() => { if (newFaq.question && newFaq.answer) { setCurrentBrief({ ...currentBrief, faqs: [...(currentBrief.faqs || []), { ...newFaq }] }); setNewFaq({ question: "", answer: "" }); } }}><Plus className="w-4 h-4 mr-1" /> Add FAQ</Button>
            </div>
          </div>

          {/* Objection Responses */}
          <div>
            <Label>Objection Responses</Label>
            <div className="space-y-2 mb-2">
              {(currentBrief.objection_responses || []).map((o, i) => (
                <div key={i} className="bg-amber-50 border border-amber-200 rounded p-3 relative">
                  <button onClick={() => { const arr = [...currentBrief.objection_responses]; arr.splice(i, 1); setCurrentBrief({ ...currentBrief, objection_responses: arr }); }} className="absolute top-2 right-2 text-slate-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                  <p className="text-xs font-semibold text-amber-700">Objection: {o.objection}</p>
                  <p className="text-xs text-slate-600 mt-1">Response: {o.response}</p>
                </div>
              ))}
            </div>
            <div className="grid gap-2">
              <Input value={newObjection.objection} onChange={(e) => setNewObjection({ ...newObjection, objection: e.target.value })} placeholder="Objection..." />
              <Textarea value={newObjection.response} onChange={(e) => setNewObjection({ ...newObjection, response: e.target.value })} placeholder="Response..." rows={2} />
              <Button size="sm" variant="outline" onClick={() => { if (newObjection.objection && newObjection.response) { setCurrentBrief({ ...currentBrief, objection_responses: [...(currentBrief.objection_responses || []), { ...newObjection }] }); setNewObjection({ objection: "", response: "" }); } }}><Plus className="w-4 h-4 mr-1" /> Add Objection</Button>
            </div>
          </div>

          {/* Do Not Say */}
          <div>
            <Label className="text-red-600">DO NOT SAY</Label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {(currentBrief.do_not_say || []).map((d, i) => (
                <Badge key={i} variant="destructive" className="gap-1">
                  {d}
                  <button onClick={() => { const arr = [...currentBrief.do_not_say]; arr.splice(i, 1); setCurrentBrief({ ...currentBrief, do_not_say: arr }); }} className="ml-1"><Trash2 className="w-3 h-3" /></button>
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input value={newDoNotSay} onChange={(e) => setNewDoNotSay(e.target.value)} placeholder="e.g., Don't mention construction delays" onKeyDown={(e) => { if (e.key === "Enter" && newDoNotSay.trim()) { setCurrentBrief({ ...currentBrief, do_not_say: [...(currentBrief.do_not_say || []), newDoNotSay.trim()] }); setNewDoNotSay(""); } }} />
              <Button size="sm" variant="outline" onClick={() => { if (newDoNotSay.trim()) { setCurrentBrief({ ...currentBrief, do_not_say: [...(currentBrief.do_not_say || []), newDoNotSay.trim()] }); setNewDoNotSay(""); } }}><Plus className="w-4 h-4" /></Button>
            </div>
          </div>

          <Button onClick={saveBrief} data-testid="save-brief-btn"><Save className="w-4 h-4 mr-2" /> Save Project Brief</Button>
        </div>
      )}
    </CardContent>
  </Card>
);

/* ─── Role Selector Component ─── */
const ROLES = [
  { value: "all", label: "All Roles" },
  { value: "telecaller", label: "Telecaller" },
  { value: "sales_rep", label: "Sales Rep" },
  { value: "sales_manager", label: "Sales Manager" },
  { value: "sales_head", label: "Sales Head" },
  { value: "admin", label: "Admin" },
];

const RoleSelector = ({ selected, onChange }) => (
  <div className="flex flex-wrap gap-2">
    {ROLES.map(role => (
      <label key={role.value} className="flex items-center gap-1.5 text-xs cursor-pointer">
        <Checkbox
          checked={selected.includes(role.value)}
          onCheckedChange={(checked) => {
            if (role.value === "all") {
              onChange(checked ? ["all"] : []);
            } else {
              const filtered = selected.filter(r => r !== "all" && r !== role.value);
              onChange(checked ? [...filtered, role.value] : filtered);
            }
          }}
        />
        {role.label}
      </label>
    ))}
  </div>
);

/* ─── Strategies Tab Sub-component ─── */
const StrategiesTab = ({ rules, editingRule, setEditingRule, newRule, setNewRule, saveRule, deleteRule, stats }) => {
  const currentRule = editingRule || newRule;
  const setCurrentRule = editingRule ? setEditingRule : setNewRule;
  
  const updateCondition = (key, value) => {
    setCurrentRule({
      ...currentRule,
      conditions: { ...currentRule.conditions, [key]: value }
    });
  };
  
  const updateStrategy = (key, value) => {
    setCurrentRule({
      ...currentRule,
      strategy: { ...currentRule.strategy, [key]: value }
    });
  };
  
  const addToStrategyList = (key, value) => {
    if (!value.trim()) return;
    const list = currentRule.strategy[key] || [];
    updateStrategy(key, [...list, value.trim()]);
  };

  return (
    <div className="space-y-4">
      {/* Stats Summary */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 border border-indigo-200 rounded-lg p-3">
            <p className="text-2xl font-bold text-indigo-700">{stats.rules?.total || 0}</p>
            <p className="text-xs text-indigo-600">Total Strategies</p>
          </div>
          <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200 rounded-lg p-3">
            <p className="text-2xl font-bold text-emerald-700">{stats.rules?.active || 0}</p>
            <p className="text-xs text-emerald-600">Active</p>
          </div>
          <div className="bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-200 rounded-lg p-3">
            <p className="text-2xl font-bold text-amber-700">{stats.success_examples?.total || 0}</p>
            <p className="text-xs text-amber-600">Success Examples</p>
          </div>
          <div className="bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 rounded-lg p-3">
            <p className="text-2xl font-bold text-slate-700">{Object.keys(stats.success_examples?.by_category || {}).length}</p>
            <p className="text-xs text-slate-600">Categories</p>
          </div>
        </div>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Target className="w-5 h-5 text-indigo-500" />
            {editingRule ? "Edit Strategy Rule" : "Create Re-engagement Strategy"}
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Define conditions and strategies for AI to recommend when engaging leads.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Strategy Name *</Label>
              <Input
                value={currentRule.name}
                onChange={(e) => setCurrentRule({ ...currentRule, name: e.target.value })}
                placeholder="e.g., Re-engage Hot Leads After 14 Days"
                data-testid="strategy-name-input"
              />
            </div>
            <div>
              <Label>Priority (higher = more important)</Label>
              <Input
                type="number"
                min="1"
                max="100"
                value={currentRule.priority}
                onChange={(e) => setCurrentRule({ ...currentRule, priority: parseInt(e.target.value) || 50 })}
              />
            </div>
          </div>

          <div>
            <Label>Description</Label>
            <Textarea
              value={currentRule.description}
              onChange={(e) => setCurrentRule({ ...currentRule, description: e.target.value })}
              placeholder="When should this strategy be applied?"
              rows={2}
            />
          </div>

          <div>
            <Label className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4" /> Target Roles
            </Label>
            <RoleSelector
              selected={currentRule.target_roles || ["all"]}
              onChange={(roles) => setCurrentRule({ ...currentRule, target_roles: roles })}
            />
          </div>

          {/* Conditions */}
          <div className="border-t pt-4">
            <Label className="flex items-center gap-2 mb-3">
              <Filter className="w-4 h-4 text-amber-500" /> Conditions (When to apply)
            </Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-slate-50 p-4 rounded-lg">
              <div>
                <Label className="text-xs">Lead Score</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {["hot", "warm", "nurture", "cold", "dormant"].map(score => (
                    <label key={score} className="flex items-center gap-1 text-xs">
                      <Checkbox
                        checked={(currentRule.conditions?.lead_score || []).includes(score)}
                        onCheckedChange={(checked) => {
                          const list = currentRule.conditions?.lead_score || [];
                          updateCondition("lead_score", checked ? [...list, score] : list.filter(s => s !== score));
                        }}
                      />
                      <span className="capitalize">{score}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs">Lead Stage</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {["new", "contacted", "qualified", "site_visit", "negotiation", "proposal"].map(stage => (
                    <label key={stage} className="flex items-center gap-1 text-xs">
                      <Checkbox
                        checked={(currentRule.conditions?.lead_stage || []).includes(stage)}
                        onCheckedChange={(checked) => {
                          const list = currentRule.conditions?.lead_stage || [];
                          updateCondition("lead_stage", checked ? [...list, stage] : list.filter(s => s !== stage));
                        }}
                      />
                      <span className="capitalize">{stage.replace(/_/g, " ")}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs">Days Inactive (min)</Label>
                <Input
                  type="number"
                  value={currentRule.conditions?.days_inactive_min || ""}
                  onChange={(e) => updateCondition("days_inactive_min", e.target.value ? parseInt(e.target.value) : null)}
                  placeholder="e.g., 7"
                />
              </div>
              <div>
                <Label className="text-xs">Days Inactive (max)</Label>
                <Input
                  type="number"
                  value={currentRule.conditions?.days_inactive_max || ""}
                  onChange={(e) => updateCondition("days_inactive_max", e.target.value ? parseInt(e.target.value) : null)}
                  placeholder="e.g., 30"
                />
              </div>
            </div>
          </div>

          {/* Strategy */}
          <div className="border-t pt-4">
            <Label className="flex items-center gap-2 mb-3">
              <Zap className="w-4 h-4 text-emerald-500" /> Strategy (What to do)
            </Label>
            <div className="space-y-4 bg-emerald-50 p-4 rounded-lg">
              <div>
                <Label className="text-xs">Approach / Mindset</Label>
                <Textarea
                  value={currentRule.strategy?.approach || ""}
                  onChange={(e) => updateStrategy("approach", e.target.value)}
                  placeholder="e.g., Value-based re-engagement focusing on new inventory or price drops..."
                  rows={2}
                />
              </div>
              <div>
                <Label className="text-xs">Message Template</Label>
                <Textarea
                  value={currentRule.strategy?.message_template || ""}
                  onChange={(e) => updateStrategy("message_template", e.target.value)}
                  placeholder="Hi {name}, I noticed you were interested in {project}. We have some exciting updates..."
                  rows={3}
                />
              </div>
              <div>
                <Label className="text-xs">Call Script</Label>
                <Textarea
                  value={currentRule.strategy?.call_script || ""}
                  onChange={(e) => updateStrategy("call_script", e.target.value)}
                  placeholder="Opening: 'Hi, this is {agent} from {company}. I'm reaching out because...'"
                  rows={3}
                />
              </div>
              <div>
                <Label className="text-xs">Talking Points</Label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {(currentRule.strategy?.talking_points || []).map((tp, i) => (
                    <Badge key={i} variant="secondary" className="gap-1">
                      {tp}
                      <button onClick={() => {
                        const list = [...(currentRule.strategy?.talking_points || [])];
                        list.splice(i, 1);
                        updateStrategy("talking_points", list);
                      }} className="ml-1 text-slate-400 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
                    </Badge>
                  ))}
                </div>
                <Input
                  placeholder="Add talking point..."
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && e.target.value.trim()) {
                      addToStrategyList("talking_points", e.target.value);
                      e.target.value = "";
                    }
                  }}
                />
              </div>
              <div>
                <Label className="text-xs">Offer Suggestions</Label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {(currentRule.strategy?.offer_suggestions || []).map((offer, i) => (
                    <Badge key={i} variant="outline" className="gap-1 bg-white">
                      {offer}
                      <button onClick={() => {
                        const list = [...(currentRule.strategy?.offer_suggestions || [])];
                        list.splice(i, 1);
                        updateStrategy("offer_suggestions", list);
                      }} className="ml-1 text-slate-400 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
                    </Badge>
                  ))}
                </div>
                <Input
                  placeholder="e.g., Offer free site visit, Share new payment plan..."
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && e.target.value.trim()) {
                      addToStrategyList("offer_suggestions", e.target.value);
                      e.target.value = "";
                    }
                  }}
                />
              </div>
              <div>
                <Label className="text-xs">Timing Advice</Label>
                <Input
                  value={currentRule.strategy?.timing_advice || ""}
                  onChange={(e) => updateStrategy("timing_advice", e.target.value)}
                  placeholder="e.g., Best time to call: 10-11 AM or 5-6 PM weekdays"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between pt-2">
            <div className="flex items-center gap-2">
              <Switch
                checked={currentRule.is_active}
                onCheckedChange={(checked) => setCurrentRule({ ...currentRule, is_active: checked })}
              />
              <span className="text-sm text-slate-600">Active</span>
            </div>
            <div className="flex gap-2">
              {editingRule && (
                <Button variant="outline" onClick={() => setEditingRule(null)}>Cancel</Button>
              )}
              <Button onClick={() => saveRule(currentRule)} data-testid="save-strategy-btn">
                <Save className="w-4 h-4 mr-2" /> {editingRule ? "Update" : "Create"} Strategy
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Existing Rules List */}
      {rules.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold">Existing Strategies ({rules.length})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {rules.map(rule => (
              <div
                key={rule.rule_id}
                className={`flex items-center gap-3 p-3 border rounded-lg ${rule.is_active ? "bg-white border-slate-200" : "bg-slate-50 border-slate-100 opacity-60"}`}
                data-testid={`strategy-rule-${rule.rule_id}`}
              >
                <div className={`w-2 h-2 rounded-full ${rule.is_active ? "bg-emerald-500" : "bg-slate-300"}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-slate-900 truncate">{rule.name}</p>
                    <Badge variant="outline" className="text-[10px]">Priority: {rule.priority}</Badge>
                  </div>
                  {rule.description && <p className="text-xs text-slate-500 truncate mt-0.5">{rule.description}</p>}
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(rule.target_roles || []).map(r => (
                      <Badge key={r} variant="secondary" className="text-[9px] h-4">{r}</Badge>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setEditingRule(rule)}>
                    <Eye className="w-4 h-4 text-slate-400" />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-400 hover:text-red-600" onClick={() => deleteRule(rule.rule_id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

/* ─── Success Examples Tab Sub-component ─── */
const SuccessExamplesTab = ({ examples, newExample, setNewExample, saveExample, deleteExample, stats }) => {
  const addKeyPhrase = (phrase) => {
    if (!phrase.trim()) return;
    setNewExample({ ...newExample, key_phrases: [...(newExample.key_phrases || []), phrase.trim()] });
  };

  const addTag = (tag) => {
    if (!tag.trim()) return;
    setNewExample({ ...newExample, tags: [...(newExample.tags || []), tag.trim()] });
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-500" />
            Add Success Example
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Document what worked so the AI can learn from successful approaches.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={newExample.title}
                onChange={(e) => setNewExample({ ...newExample, title: e.target.value })}
                placeholder="e.g., Converted cold lead with site visit offer"
                data-testid="example-title-input"
              />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={newExample.category} onValueChange={(v) => setNewExample({ ...newExample, category: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="re-engagement">Re-engagement</SelectItem>
                  <SelectItem value="objection_handling">Objection Handling</SelectItem>
                  <SelectItem value="closing">Closing</SelectItem>
                  <SelectItem value="follow_up">Follow-up</SelectItem>
                  <SelectItem value="first_contact">First Contact</SelectItem>
                  <SelectItem value="site_visit">Site Visit</SelectItem>
                  <SelectItem value="negotiation">Negotiation</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4" /> Target Roles (who should learn this)
            </Label>
            <RoleSelector
              selected={newExample.target_roles || ["all"]}
              onChange={(roles) => setNewExample({ ...newExample, target_roles: roles })}
            />
          </div>

          <div>
            <Label>Approach Used</Label>
            <Textarea
              value={newExample.approach_used}
              onChange={(e) => setNewExample({ ...newExample, approach_used: e.target.value })}
              placeholder="Describe the approach/strategy you used..."
              rows={2}
            />
          </div>

          <div>
            <Label>Message / Script Sent</Label>
            <Textarea
              value={newExample.message_sent}
              onChange={(e) => setNewExample({ ...newExample, message_sent: e.target.value })}
              placeholder="The actual message or call script that worked..."
              rows={3}
            />
          </div>

          <div>
            <Label>Key Phrases That Worked</Label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {(newExample.key_phrases || []).map((phrase, i) => (
                <Badge key={i} variant="secondary" className="gap-1">
                  "{phrase}"
                  <button onClick={() => {
                    const list = [...(newExample.key_phrases || [])];
                    list.splice(i, 1);
                    setNewExample({ ...newExample, key_phrases: list });
                  }} className="ml-1 text-slate-400 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
                </Badge>
              ))}
            </div>
            <Input
              placeholder="e.g., 'exclusive preview', 'limited availability'..."
              onKeyDown={(e) => {
                if (e.key === "Enter" && e.target.value.trim()) {
                  addKeyPhrase(e.target.value);
                  e.target.value = "";
                }
              }}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Outcome / Result</Label>
              <Input
                value={newExample.outcome?.result || ""}
                onChange={(e) => setNewExample({ ...newExample, outcome: { ...newExample.outcome, result: e.target.value } })}
                placeholder="e.g., Booked site visit, Converted to booking"
              />
            </div>
            <div>
              <Label>Why It Worked</Label>
              <Input
                value={newExample.why_it_worked}
                onChange={(e) => setNewExample({ ...newExample, why_it_worked: e.target.value })}
                placeholder="e.g., Created urgency, addressed their specific concern"
              />
            </div>
          </div>

          <div>
            <Label>Tags</Label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {(newExample.tags || []).map((tag, i) => (
                <Badge key={i} variant="outline" className="gap-1">
                  #{tag}
                  <button onClick={() => {
                    const list = [...(newExample.tags || [])];
                    list.splice(i, 1);
                    setNewExample({ ...newExample, tags: list });
                  }} className="ml-1 text-slate-400 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
                </Badge>
              ))}
            </div>
            <Input
              placeholder="Add tags for categorization..."
              onKeyDown={(e) => {
                if (e.key === "Enter" && e.target.value.trim()) {
                  addTag(e.target.value);
                  e.target.value = "";
                }
              }}
            />
          </div>

          <Button onClick={saveExample} data-testid="save-example-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Success Example
          </Button>
        </CardContent>
      </Card>

      {/* Existing Examples List */}
      {examples.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold">Success Library ({examples.length})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {examples.map(ex => (
              <div
                key={ex.example_id}
                className="bg-gradient-to-r from-amber-50 to-white border border-amber-200 rounded-lg p-4"
                data-testid={`success-example-${ex.example_id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-slate-900">{ex.title}</p>
                      <Badge variant="secondary" className="text-[10px]">{ex.category?.replace(/_/g, " ")}</Badge>
                    </div>
                    {ex.approach_used && (
                      <p className="text-xs text-slate-600 mt-1"><strong>Approach:</strong> {ex.approach_used}</p>
                    )}
                    {ex.message_sent && (
                      <p className="text-xs text-slate-500 mt-1 italic">"{ex.message_sent.substring(0, 150)}{ex.message_sent.length > 150 ? "..." : ""}"</p>
                    )}
                    {ex.key_phrases?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {ex.key_phrases.map((phrase, i) => (
                          <Badge key={i} variant="outline" className="text-[9px] bg-white">"{phrase}"</Badge>
                        ))}
                      </div>
                    )}
                    {ex.outcome?.result && (
                      <p className="text-xs text-emerald-600 mt-2 font-medium">Outcome: {ex.outcome.result}</p>
                    )}
                    {ex.why_it_worked && (
                      <p className="text-xs text-slate-500 mt-1">Why it worked: {ex.why_it_worked}</p>
                    )}
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(ex.target_roles || []).map(r => (
                        <Badge key={r} variant="secondary" className="text-[9px] h-4">{r}</Badge>
                      ))}
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-400 hover:text-red-600 flex-shrink-0" onClick={() => deleteExample(ex.example_id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {examples.length === 0 && (
        <div className="text-center py-8 text-slate-400 bg-slate-50 rounded-lg border border-dashed border-slate-200">
          <Trophy className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p className="text-sm">No success examples yet</p>
          <p className="text-xs mt-1">Add examples of what worked so AI can learn from them</p>
        </div>
      )}
    </div>
  );
};

export default AITrainingPage;
